/**
 * ⟦A-97 §3⟧ **L'historique minimal du bac à sable.**
 *
 * > A-97 §3 : « Mode compte d'abord (c'est là que l'abandon était impossible sans `curl`). Le
 * > bac à sable a ses runs locales : un historique minimal y suffit, sans mémorial étendu. »
 *
 * ## Le problème que ce fichier résout
 *
 * Le bac à sable garde **une** run, sous une seule clé (`maxime.bac-a-sable.run.v1`), et
 * `LocalGameClient.abandonner()` fait un `localStorage.removeItem` : la partie n'est pas
 * archivée, elle est **détruite**. Côté compte, une run finie devient un mémorial (PLAN §4.11)
 * ; côté bac à sable, elle n'existait plus du tout — pas de liste, rien à relire, aucune trace
 * qu'on ait joué.
 *
 * D'où une **seconde clé**, indépendante de celle de la partie : les fiches survivent à
 * l'effacement de la run parce qu'elles ne vivent pas au même endroit. C'est ce qui permet à la
 * promesse faite au joueur de rester vraie des deux côtés à la fois — *« la partie est effacée
 * de ce navigateur »* (elle l'est) et *« tu retrouveras la fiche dans ton historique »* (aussi).
 *
 * ## Ce que ce n'est pas
 *
 * **Pas un mémorial.** On ne garde ni l'état, ni le journal, ni les vidéos, ni l'épitaphe — six
 * champs, le strict nécessaire pour une ligne de liste. « Sans mémorial étendu », dit
 * l'avenant : conserver le `GameState` entier de chaque partie finie ferait grossir le
 * `localStorage` sans limite pour un écran qui n'en montrerait rien.
 *
 * **Pas une sauvegarde.** Une fiche ne se rejoue pas, ne se restaure pas. Le bac à sable dit
 * déjà que rien n'y est vérifié ni sauvegardé ailleurs — ceci ne le change pas.
 *
 * ## Pourquoi le fichier est coupé en deux
 *
 * `vitest.config.ts` du client le pose en règle : environnement `node`, pas de jsdom, « un
 * module qui aurait besoin d'un DOM pour être testé n'est pas pur, et c'est le test qui a
 * raison ». Tout ce qui **décide** vit donc dans des fonctions pures — `ficheDeRun` (ce qu'on
 * garde d'une run), `rangerFiches` (dédoublonnage, ordre, borne), `raisonDeFinLocale` (la
 * traduction moteur → base) — et elles sont testées à ce titre. Les deux fonctions qui touchent
 * `localStorage` (`historiqueLocal`, `archiverRunLocale`) ne font plus que lire, appeler, et
 * écrire : il n'y reste aucune décision à se tromper.
 */

import type { GameState, RunMode } from "@maxime/shared";
import { jour } from "@maxime/shared";

import type { RunSnapshot } from "./gameClient";

/**
 * Clé de persistance, **distincte de celle de la run** (`maxime.bac-a-sable.run.v1`).
 *
 * C'est tout le mécanisme : `abandonner()` efface la run, il n'efface pas cette clé-là.
 * Versionnée pour la même raison que l'autre — un changement de forme jette proprement
 * l'ancien contenu plutôt que de le rendre à moitié.
 */
const CLE = "maxime.bac-a-sable.historique.v1";

/**
 * Combien de fiches on garde. Borne volontaire : `localStorage` a un quota (~5 Mo par origine,
 * partagé avec la partie en cours), et une liste sans fin finirait par empêcher d'écrire la run
 * elle-même. Les plus anciennes sortent en premier.
 */
const MAX_FICHES = 50;

/** Une chaîne de bac à sable, refermée. Six champs, et pas un de plus. */
export interface FicheLocale {
  runId: string;
  channelName: string;
  mode: RunMode;
  /**
   * Le vocabulaire de la BASE (`ended_reason`), pas celui du moteur — pour que l'écran lise ces
   * fiches avec exactement la même table que les runs de compte (`libelleFin`, `game/fins.ts`).
   * Traduire ici plutôt qu'à l'affichage, c'est n'avoir qu'un seul vocabulaire à l'écran.
   */
  endedReason: string;
  subs: number;
  /** Durée **simulée** (jours de jeu), jamais le temps réel écoulé. */
  daysSimulated: number;
  videosCount: number;
  /** Horodatage réel de la fin — c'est une métadonnée de rangement, pas une horloge de jeu. */
  finieLe: number;
}

/**
 * `GameOverId` (moteur) → `ended_reason` (vocabulaire de la base).
 *
 * ⚠️ **C'est le jumeau CLIENT de `RAISON_PAR_GAME_OVER` (`server/src/progression.ts`)**, et il
 * est assumé : le bac à sable n'a pas de serveur, donc il refait la traduction que le serveur
 * ferait — exactement comme `localGameClient` refait déjà `progressionDe`. Les deux tables
 * doivent bouger ensemble ; celle du serveur est **exhaustive par le type** (`Record<
 * GameOverEtendu, EndedReason>`, le compilateur y refuse une fin non traitée), c'est donc elle
 * qui alerte en premier quand une sixième fin naît.
 *
 * ⟦C60 · A-177⟧ Le grand ménage a coupé la faillite, les strikes et le bad buzz comme sanction :
 * `spirale_reputation`, `bannissement` et `faillite` ne tombent plus (`gameOversPossibles`,
 * `game/fins.ts`), donc une NOUVELLE fiche d'historique ne porte plus `reputation`, `ban` ni
 * `bankruptcy`. Les lignes restent : elles traduisent une chaîne d'avant déjà au `game_over`, et
 * l'Historique relit ses anciennes fiches par `libelleFin` (`FINS_ANCIENNES`).
 */
const RAISON_PAR_GAME_OVER: Record<string, string> = {
  burnout_terminal: "burnout",
  spirale_reputation: "reputation",
  bannissement: "ban",
  faillite: "bankruptcy",
  fantome: "extinguished",
};

/**
 * La raison de fin d'un état de bac à sable — **même ordre de tests que `raisonDeFin` côté
 * serveur** (`server/src/progression.ts`), et l'ordre est ce qui compte :
 *
 * 1. Une run **close après le million** dit `victory_closed`, jamais une raison de défaite.
 * 2. Un game over d'**après** le million dit `legend_death` : la victoire est gravée, la run se
 *    termine en légende, pas en échec. Une run peut porter les deux — d'où le test dans cet
 *    ordre-là, et pas dans l'autre.
 *
 * `null` quand l'état n'est pas terminal : rien à archiver.
 */
export function raisonDeFinLocale(state: GameState): string | null {
  if (state.status === "ended" && state.victory_locked) return "victory_closed";
  if (state.game_over) {
    return state.victory_locked ? "legend_death" : (RAISON_PAR_GAME_OVER[state.game_over] ?? state.game_over);
  }
  // Une run `ended` sans victoire gravée : le seul chemin qui y mène en bac à sable est
  // l'abandon, et il passe par `archiverRunLocale(run, "abandoned")` — jamais par ici.
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Ce qui décide — pur, testé, sans navigateur
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Ce qu'on garde d'une run qui se termine. **Pur** : l'instant est passé en paramètre, jamais
 * lu de l'horloge système — c'est la règle du moteur, et elle vaut aussi pour ce qui l'entoure.
 *
 * `raison` est une `ended_reason` (vocabulaire de la base), pas un `GameOverId` : les appelants
 * passent par `raisonDeFinLocale`, ou `"abandoned"` en dur pour l'abandon — c'est la seule fin
 * que le moteur ne connaît pas, parce qu'elle n'est pas une règle de jeu.
 */
export function ficheDeRun(run: RunSnapshot, raison: string, maintenant: number): FicheLocale {
  return {
    runId: run.runId,
    channelName: run.channelName,
    mode: run.mode,
    endedReason: raison,
    // Même arrondi que l'épitaphe côté serveur : les jalons versent un pourcentage des abonnés
    // courants, donc `subscribers` n'est pas garanti entier.
    subs: Math.round(run.state.subscribers),
    daysSimulated: jour(run.state.clock_h),
    videosCount: run.state.videos.length,
    finieLe: maintenant,
  };
}

/**
 * Range une fiche neuve dans la liste : **dédoublonnage par `runId`**, plus récente d'abord,
 * liste bornée. Pur.
 *
 * L'idempotence par `runId` n'est pas un détail de confort : une run est archivée **deux fois**
 * dans le cas le plus courant — le game over posé par `#ecrire`, puis l'abandon déclenché
 * depuis l'écran de fin. Sans elle, ce sont précisément les parties qu'on a regardées mourir
 * avant de cliquer qui apparaîtraient en double.
 */
export function rangerFiches(
  existantes: readonly FicheLocale[],
  nouvelle: FicheLocale,
): FicheLocale[] {
  return [nouvelle, ...existantes.filter((f) => f.runId !== nouvelle.runId)]
    .sort((a, b) => b.finieLe - a.finieLe)
    .slice(0, MAX_FICHES);
}

// ─────────────────────────────────────────────────────────────────────────────
// Ce qui stocke — aucune décision, seulement des allers-retours vers `localStorage`
// ─────────────────────────────────────────────────────────────────────────────

/** Les fiches rangées, **la plus récente d'abord**. Un stockage illisible rend une liste vide. */
export function historiqueLocal(): FicheLocale[] {
  try {
    const brut = localStorage.getItem(CLE);
    if (!brut) return [];
    const fiches = JSON.parse(brut) as FicheLocale[];
    if (!Array.isArray(fiches)) return [];
    // Le tri est refait à la lecture : l'ordre du stockage n'est pas un contrat.
    return [...fiches].sort((a, b) => b.finieLe - a.finieLe);
  } catch {
    // JSON cassé, quota, mode privé : on rend une liste vide plutôt que de casser l'écran.
    return [];
  }
}

/**
 * Archive une run de bac à sable qui se termine.
 *
 * ⚠️ **À appeler AVANT le `localStorage.removeItem` de la run** : après, il n'y a plus rien à
 * lire — ni abonnés, ni jours, ni vidéos. C'est le piège que ce fichier existe pour éviter, et
 * l'ordre est tenu par l'appelant (`LocalGameClient.abandonner()`, qui le dit en toutes
 * lettres). Ce qui le rend POSSIBLE, en revanche, est ici : `CLE` n'est pas la clé de la run.
 */
export function archiverRunLocale(run: RunSnapshot, raison: string): void {
  const fiche = ficheDeRun(run, raison, Date.now());
  try {
    localStorage.setItem(CLE, JSON.stringify(rangerFiches(historiqueLocal(), fiche)));
  } catch {
    // Quota plein ou mode privé : perdre une fiche d'historique ne doit jamais empêcher une
    // partie de se terminer. On échoue en silence, comme `#ecrire` le fait déjà pour la run.
  }
}
